import axios from 'axios'
import { getTprmApiBaseUrl } from '@/utils/backendEnv'
import { shouldAvoidLoginRedirect, requestAuthFromParent } from '@/utils/embedContext'
import { clearCachedUserRole } from '@/services/roleRoutingService'
import { tokenService } from '@/services/tokenService'

const stripTrailingSlash = (value = '') => value.replace(/\/+$/, '')

/**
 * MFA auth API base path.
 * - Override with VITE_TPRM_AUTH_BASE_URL (e.g. http://127.0.0.1:8000/api/auth or /api/auth).
 * - Default: {getTprmApiBaseUrl()}/auth → /api/tprm/auth when using dev proxy.
 * Some Django entrypoints only mounted /api/auth/; we retry on HTML 404 below.
 */
const explicitAuthBase = (import.meta.env.VITE_TPRM_AUTH_BASE_URL || '').trim()
const API_BASE_URL = explicitAuthBase
  ? stripTrailingSlash(explicitAuthBase)
  : `${stripTrailingSlash(getTprmApiBaseUrl())}/auth`

// Create axios instance with default config
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
})

// Request interceptor to add token using tokenService
authApi.interceptors.request.use(
  (config) => {
    // Allow endpoints to explicitly opt-out of auth (e.g., set-first-password)
    if (config.headers && 'Authorization' in config.headers && config.headers.Authorization === undefined) {
      delete config.headers.Authorization
      return config
    }
    const token = tokenService.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      if (shouldAvoidLoginRedirect()) {
        requestAuthFromParent()
        return Promise.reject(error)
      }
      // Use tokenService to clear all auth tokens
      tokenService.clearTokens()
      localStorage.removeItem('current_user')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export default {
  /**
   * Step 1: Login with username and password, receive OTP via email
   */
  async login(username, password) {
    try {
      const response = await authApi.post('/login/', {
        username,
        password
      })
      const data = response.data || {}
      return {
        success: true,
        data,
        requiresOtp: Boolean(data.requires_otp),
        requiresPasswordChange: Boolean(data.requires_password_change),
        user: data.user,
        message: data.message
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.',
        details: error.response?.data
      }
    }
  },

  /**
   * First-time login: set permanent password and initiate OTP
   */
  async setFirstPassword(username, tempPassword, newPassword) {
    try {
      const response = await authApi.post('/set-first-password/', {
        username,
        temp_password: tempPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: undefined }
      })
      const data = response.data || {}
      return {
        success: true,
        data,
        requiresOtp: Boolean(data.requires_otp),
        user: data.user,
        message: data.message
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update password. Please try again.',
        details: error.response?.data
      }
    }
  },

  /**
   * Step 2: Verify OTP code
   */
  async verifyOtp(username, otp) {
    try {
      const response = await authApi.post('/verify-otp/', {
        username,
        otp
      })
      
      if (response.data.success) {
        // Store authentication data using tokenService for secure token management
        const accessToken = response.data.access_token || response.data.session_token
        const refreshToken = response.data.refresh_token

        // Store access token in localStorage so all API clients (which read
        // localStorage.getItem('session_token')) can attach the Authorization header.
        if (accessToken) {
          tokenService.setToken(accessToken, false) // false = use localStorage
        }
        if (refreshToken) {
          tokenService.setRefreshToken(refreshToken)
        }

        // Store user data
        localStorage.setItem('current_user', JSON.stringify(response.data.user))

        // Store tenant_id separately for profile and other tenant-scoped requests
        if (response.data.user?.tenant_id) {
          localStorage.setItem('tenant_id', String(response.data.user.tenant_id))
        }
      }
      
      return {
        success: true,
        data: response.data,
        user: response.data.user,
        token: response.data.session_token || response.data.access_token
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'OTP verification failed. Please try again.',
        details: error.response?.data
      }
    }
  },

  /**
   * Resend OTP code
   */
  async resendOtp(username) {
    try {
      const response = await authApi.post('/resend-otp/', {
        username
      })
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to resend OTP. Please try again.',
        details: error.response?.data
      }
    }
  },

  /**
   * Validate current session
   */
  async validateSession() {
    try {
      const response = await authApi.get('/validate-session/')
      return {
        success: true,
        user: response.data.user
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Session validation failed'
      }
    }
  },

  /**
   * Refresh access token
   */
  async refreshToken() {
    try {
      const refreshToken = tokenService.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authApi.post('/refresh-token/', {
        refresh_token: refreshToken
      })

      if (response.data.success) {
        const newToken = response.data.access_token
        // Store new access token in localStorage (consistent with login storage)
        tokenService.setToken(newToken, false)
      }

      return {
        success: true,
        token: response.data.access_token
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Token refresh failed'
      }
    }
  },

  /**
   * Logout user
   */
  async logout() {
    try {

      const response = await authApi.post('/logout/')

      // Clear local storage
      localStorage.removeItem('session_token')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('current_user')
      localStorage.removeItem('tenant_id')
      clearCachedUserRole()

      return {
        success: true,
        message: response.data?.message || 'Logged out successfully'
      }
    } catch (error) {

      // Still clear local storage even if API call fails
      localStorage.removeItem('session_token')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('current_user')
      localStorage.removeItem('tenant_id')
      clearCachedUserRole()

      return {
        success: false,
        error: error.response?.data?.message || 'Logout API call failed, but local storage cleared'
      }
    }
  },

  /**
   * Get MFA status for a user
   */
  async getMfaStatus(username) {
    try {
      const response = await authApi.get('/status/', {
        params: { username }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get MFA status'
      }
    }
  },

  /**
   * Fetch authenticated user's profile scoped by tenant.
   */
  async getUserProfile(tenantId) {
    try {
      const resolvedTenantId = tenantId || localStorage.getItem('tenant_id')
      if (!resolvedTenantId) {
        return {
          success: false,
          error: 'Tenant ID not found in session'
        }
      }

      const response = await authApi.get(`/profile/${resolvedTenantId}/`)
      return {
        success: true,
        data: response.data?.data || response.data
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to load user profile',
        details: error.response?.data
      }
    }
  },

  /**
   * Update authenticated user's profile scoped by tenant.
   */
  async updateUserProfile(payload, tenantId) {
    try {
      const resolvedTenantId = tenantId || localStorage.getItem('tenant_id')
      if (!resolvedTenantId) {
        return {
          success: false,
          error: 'Tenant ID not found in session'
        }
      }

      const response = await authApi.put(`/profile/${resolvedTenantId}/`, payload || {})

      // Keep local current_user in sync for header and other screens
      const updated = response.data?.data
      if (updated) {
        const current = this.getCurrentUser() || {}
        const merged = {
          ...current,
          id: updated.userid ?? current.id,
          userid: updated.userid ?? current.userid,
          username: updated.username ?? current.username,
          email: updated.email ?? current.email,
          first_name: updated.first_name ?? current.first_name,
          last_name: updated.last_name ?? current.last_name,
          tenant_id: updated.tenant_id ?? current.tenant_id,
        }
        localStorage.setItem('current_user', JSON.stringify(merged))
      }

      return {
        success: true,
        data: updated || response.data
      }
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update user profile',
        details: error.response?.data
      }
    }
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    try {
      let userStr = localStorage.getItem('current_user')
      if (!userStr) userStr = localStorage.getItem('user')
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {

      return null
    }
  },

  /**
   * Get current session token
   */
  getSessionToken() {
    return tokenService.getToken() || ''
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    try {
      if (sessionStorage.getItem('tprm_mfa_pending') === '1') {
        return false
      }
    } catch (e) {
      /* ignore */
    }
    const token = this.getSessionToken()
    const user = this.getCurrentUser()
    return !!(token && user)
  },

  /**
   * Step 1: Forgot password - request OTP
   */
  async forgotPassword(username) {
    try {
      const response = await authApi.post('/forgot-password/', {
        username
      })
      return {
        success: true,
        message: response.data.message,
        username: response.data.username,
        email: response.data.email
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send OTP. Please try again.'
      }
    }
  },

  /**
   * Step 2: Verify OTP for forgot password
   */
  async verifyForgotPasswordOtp(username, otp) {
    try {
      const response = await authApi.post('/verify-forgot-password-otp/', {
        username,
        otp
      })
      return {
        success: true,
        message: response.data.message,
        resetToken: response.data.reset_token
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'OTP verification failed. Please try again.'
      }
    }
  },

  /**
   * Step 3: Reset password with new password
   */
  async resetPassword(resetToken, newPassword, confirmPassword) {
    try {
      const response = await authApi.post('/reset-password/', {
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      })
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Password reset failed. Please try again.'
      }
    }
  },

  /**
   * SSO: Get the authorize URL from the backend (Microsoft or Google)
   */
  async getSsoAuthorizeUrl(provider, redirectUri) {
    try {
      const queryParts = [`provider=${encodeURIComponent(provider)}`]
      if (redirectUri) {
        queryParts.push(`redirect_uri=${encodeURIComponent(redirectUri)}`)
      }
      const response = await authApi.get(`/sso/init/?${queryParts.join('&')}`)
      return {
        success: true,
        authorizeUrl: response.data.authorize_url,
        redirectUri: response.data.redirect_uri,
        provider: response.data.provider
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'SSO is not available.'
      }
    }
  },

  /**
   * SSO: Exchange the authorization code for tokens
   */
  async ssoCallback(provider, code, redirectUri) {
    try {
      const response = await authApi.post('/sso/callback/', {
        provider,
        code,
        redirect_uri: redirectUri
      })

      if (response.data.success) {
        const accessToken = response.data.access_token || response.data.session_token
        const refreshToken = response.data.refresh_token

        // Store access token in localStorage so all API clients (which read
        // localStorage.getItem('session_token')) can attach the Authorization header.
        if (accessToken) {
          tokenService.setToken(accessToken, false) // false = use localStorage
        }
        if (refreshToken) {
          tokenService.setRefreshToken(refreshToken)
        }

        // Store user data
        localStorage.setItem('current_user', JSON.stringify(response.data.user))
        if (response.data.user?.tenant_id) {
          localStorage.setItem('tenant_id', String(response.data.user.tenant_id))
        }
      }

      return {
        success: true,
        data: response.data,
        user: response.data.user,
        token: response.data.session_token || response.data.access_token
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'SSO authentication failed. Please try again.'
      }
    }
  }
}

